import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import axios from 'axios';

/* ── ícone reutilizável ── */
const Icon = ({ d, size = 16, color = 'currentColor', stroke = 2 }) => (
  <svg width={size} height={size} fill="none" stroke={color} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={stroke} d={d} />
  </svg>
);

const ICONS = {
  chevron:  'M19 9l-7 7-7-7',
  filter:   'M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z',
  refresh:  'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  check:    'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  warn:     'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  money:    'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
  clock:    'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  alert:    'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  chart:    'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  shield:   'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  empty:    'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
};

/* ── helpers de estilo ── */
const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: '8px',
  border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151',
  fontFamily: "'Inter','Segoe UI',sans-serif", outline: 'none',
  backgroundColor: '#fff', boxSizing: 'border-box',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};
const focusHandlers = {
  onFocus: e => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; },
  onBlur:  e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; },
};
const FieldLabel = ({ children }) => (
  <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '4px' }}>
    {children}
  </label>
);

/* ── accordion nativo ── */
const AccordionSection = ({ title, icon, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '10px' }}>
      <button
        type="button" onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', border: 'none', cursor: 'pointer',
          background: open ? 'linear-gradient(135deg, #eff6ff, #f8fafc)' : '#f9fafb',
          fontFamily: 'inherit', transition: 'background 0.15s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <Icon d={icon} size={14} color="#2563eb" />
          <span style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>{title}</span>
        </div>
        <div style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
          <Icon d={ICONS.chevron} size={14} color="#6b7280" />
        </div>
      </button>
      {open && (
        <div style={{ padding: '18px 20px', backgroundColor: '#fff', borderTop: '1px solid #f1f5f9' }}>
          {children}
        </div>
      )}
    </div>
  );
};

/* ── barra de filtros ── */
const FilterBar = ({ children, onFilter, isLoading, filterLabel = 'Filtrar' }) => (
  <div style={{
    display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end',
    marginBottom: '16px', padding: '14px 16px',
    backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9',
  }}>
    {children}
    {onFilter && (
      <div style={{ marginLeft: 'auto' }}>
        <button
          type="button" onClick={onFilter} disabled={isLoading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: isLoading ? '#93c5fd' : 'linear-gradient(135deg, #1e3a5f, #2563eb)',
            color: '#fff', fontSize: '12px', fontWeight: '600',
            cursor: isLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}
        >
          {isLoading
            ? <Spinner as="span" animation="border" size="sm" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
            : <Icon d={ICONS.filter} size={11} color="#fff" />
          }
          {isLoading ? 'Filtrando...' : filterLabel}
        </button>
      </div>
    )}
  </div>
);

/* ── loading ── */
const LoadingRow = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px', gap: '10px' }}>
    <Spinner animation="border" variant="primary" style={{ width: '24px', height: '24px' }} />
    <span style={{ fontSize: '13px', color: '#6b7280' }}>Carregando...</span>
  </div>
);

/* ── empty state ── */
const EmptyState = ({ message }) => (
  <div style={{ padding: '40px 24px', textAlign: 'center' }}>
    <Icon d={ICONS.empty} size={28} color="#d1d5db" />
    <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#9ca3af', fontWeight: '500' }}>{message}</p>
  </div>
);

/* ── badge de status ── */
const StatusBadge = ({ status }) => {
  const map = {
    Recebido: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
    Glosa:    { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
    Pendente: { bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  };
  const s = map[status] || map.Pendente;
  return (
    <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '999px', backgroundColor: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {status || 'Pendente'}
    </span>
  );
};

/* ── estilos de th/td ── */
const thStyle = {
  padding: '9px 13px', fontSize: '11px', fontWeight: '700', color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.4px',
  borderBottom: '2px solid #e2e8f0', background: 'linear-gradient(135deg,#f8fafc,#f1f5f9)',
  textAlign: 'left', whiteSpace: 'nowrap',
};
const tdStyle = {
  padding: '10px 13px', fontSize: '13px', color: '#374151',
  borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle',
};

/* ════════════════════════════════════════════════════════ */

const RelatoriosPagamentosTab = ({
  currentEntidade, formatCurrency, isActive,
  onMarcarRecebidoClick, onMarcarGlosadoClick, refreshKey,
}) => {
  /* ── estados ── */
  const [pagamentosRecebidosData,     setPagamentosRecebidosData]     = useState([]);
  const [isLoadingPagamentosRecebidos, setIsLoadingPagamentosRecebidos] = useState(false);
  const [filtroDataInicioPagRecebidos, setFiltroDataInicioPagRecebidos] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [filtroDataFimPagRecebidos,    setFiltroDataFimPagRecebidos]    = useState(new Date().toISOString().split('T')[0]);

  const [pagamentosPendentesData,     setPagamentosPendentesData]     = useState([]);
  const [isLoadingPagamentosPendentes, setIsLoadingPagamentosPendentes] = useState(false);

  const [inadimplenciaData,           setInadimplenciaData]           = useState([]);
  const [isLoadingInadimplencia,      setIsLoadingInadimplencia]      = useState(false);

  const [pagPorFormaPagamentoData,    setPagPorFormaPagamentoData]    = useState({ labels: [], datasets: [] });
  const [isLoadingPagPorForma,        setIsLoadingPagPorForma]        = useState(false);
  const [filtroDataInicioFormaPag,    setFiltroDataInicioFormaPag]    = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [filtroDataFimFormaPag,       setFiltroDataFimFormaPag]       = useState(new Date().toISOString().split('T')[0]);

  const [contasReceberConvenioData,   setContasReceberConvenioData]   = useState([]);
  const [isLoadingContasReceberConvenio, setIsLoadingContasReceberConvenio] = useState(false);
  const [filtroDataInicioConvenio,    setFiltroDataInicioConvenio]    = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [filtroDataFimConvenio,       setFiltroDataFimConvenio]       = useState(new Date().toISOString().split('T')[0]);
  const [filtroConvenioNome,          setFiltroConvenioNome]          = useState('');
  const [filtroStatusRecebimento,     setFiltroStatusRecebimento]     = useState('');

  /* ── fetches ── */
  const fetchPagamentosRecebidos = async () => {
    if (!filtroDataInicioPagRecebidos || !filtroDataFimPagRecebidos || !currentEntidade) return;
    setIsLoadingPagamentosRecebidos(true);
    try {
      const r = await axios.get('/api/relatorios/pagamentos/recebidos', {
        params: { entidade_id: currentEntidade.id, data_inicio: filtroDataInicioPagRecebidos, data_fim: filtroDataFimPagRecebidos },
      });
      setPagamentosRecebidosData(r.data.dados || []);
    } catch { setPagamentosRecebidosData([]); }
    finally { setIsLoadingPagamentosRecebidos(false); }
  };

  const fetchPagamentosPendentes = async () => {
    if (!currentEntidade) return;
    setIsLoadingPagamentosPendentes(true);
    try {
      const r = await axios.get('/api/relatorios/pagamentos/pendentes', { params: { entidade_id: currentEntidade.id } });
      setPagamentosPendentesData(r.data.dados || []);
    } catch { setPagamentosPendentesData([]); }
    finally { setIsLoadingPagamentosPendentes(false); }
  };

  const fetchInadimplencia = async () => {
    if (!currentEntidade) return;
    setIsLoadingInadimplencia(true);
    try {
      const r = await axios.get('/api/relatorios/pagamentos/inadimplencia', { params: { entidade_id: currentEntidade.id } });
      setInadimplenciaData(r.data.dados || []);
    } catch { setInadimplenciaData([]); }
    finally { setIsLoadingInadimplencia(false); }
  };

  const fetchPagPorFormaPagamento = async () => {
    if (!filtroDataInicioFormaPag || !filtroDataFimFormaPag || !currentEntidade) return;
    setIsLoadingPagPorForma(true);
    try {
      const r = await axios.get('/api/relatorios/pagamentos/por-forma', {
        params: { entidade_id: currentEntidade.id, data_inicio: filtroDataInicioFormaPag, data_fim: filtroDataFimFormaPag },
      });
      setPagPorFormaPagamentoData({
        labels: r.data.labels || [],
        datasets: [{
          label: 'Faturamento por Forma de Pagamento (R$)',
          data: r.data.data || [],
          backgroundColor: ['rgba(37,99,235,0.7)', 'rgba(16,185,129,0.7)', 'rgba(245,158,11,0.7)', 'rgba(239,68,68,0.7)', 'rgba(139,92,246,0.7)'],
          borderRadius: 6,
        }],
      });
    } catch { setPagPorFormaPagamentoData({ labels: [], datasets: [] }); }
    finally { setIsLoadingPagPorForma(false); }
  };

  const fetchContasAReceberConvenio = async () => {
    if (!filtroDataInicioConvenio || !filtroDataFimConvenio || !currentEntidade) return;
    setIsLoadingContasReceberConvenio(true);
    try {
      const params = { entidade_id: currentEntidade.id, data_inicio: filtroDataInicioConvenio, data_fim: filtroDataFimConvenio };
      if (filtroConvenioNome)      params.convenio_nome       = filtroConvenioNome;
      if (filtroStatusRecebimento) params.status_recebimento  = filtroStatusRecebimento;
      const r = await axios.get('/api/relatorios/pagamentos/contas-a-receber-convenio', { params });
      setContasReceberConvenioData(r.data.dados || []);
    } catch { setContasReceberConvenioData([]); }
    finally { setIsLoadingContasReceberConvenio(false); }
  };

  useEffect(() => {
    if (isActive && currentEntidade) {
      fetchPagamentosRecebidos();
      fetchPagamentosPendentes();
      fetchInadimplencia();
      fetchPagPorFormaPagamento();
      fetchContasAReceberConvenio();
    }
  }, [isActive, currentEntidade,
      filtroDataInicioPagRecebidos, filtroDataFimPagRecebidos,
      filtroDataInicioFormaPag, filtroDataFimFormaPag,
      filtroDataInicioConvenio, filtroDataFimConvenio,
      filtroConvenioNome, filtroStatusRecebimento, refreshKey]);

  /* ── RENDER ── */
  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .pag-tr:hover { background: #fafbff; }
        @media (max-width:575px) { .hide-xs { display:none!important; } }
        @media (max-width:767px) { .hide-sm { display:none!important; } }
      `}</style>

      {/* ══ Pagamentos Recebidos ══ */}
      {/* ══ Pagamentos Recebidos ══ */}
      <AccordionSection title="Pagamentos Recebidos" icon={ICONS.check} defaultOpen>
        <FilterBar onFilter={fetchPagamentosRecebidos} isLoading={isLoadingPagamentosRecebidos}>
          <div style={{ minWidth: '150px' }}>
            <FieldLabel>Data Início</FieldLabel>
            <input
              style={inputStyle}
              type="date"
              value={filtroDataInicioPagRecebidos}
              onChange={e => setFiltroDataInicioPagRecebidos(e.target.value)}
              {...focusHandlers}
            />
          </div>
          <div style={{ minWidth: '150px' }}>
            <FieldLabel>Data Fim</FieldLabel>
            <input
              style={inputStyle}
              type="date"
              value={filtroDataFimPagRecebidos}
              onChange={e => setFiltroDataFimPagRecebidos(e.target.value)}
              {...focusHandlers}
            />
          </div>
        </FilterBar>

        {isLoadingPagamentosRecebidos ? <LoadingRow /> : pagamentosRecebidosData.length === 0 ? (
          <EmptyState message="Nenhum pagamento recebido para o período." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle} className="hide-sm">Data Pag.</th>
                  <th style={thStyle}>Paciente</th>
                  <th style={thStyle} className="hide-sm">Serviço(s)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Valor Pago</th>
                  <th style={thStyle} className="hide-xs">Forma Pag.</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pagamentosRecebidosData.map(item => (
                  <tr key={item.id} className="pag-tr">
                    <td style={tdStyle} className="hide-sm">
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>
                        {item.data_pagamento}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>{item.paciente_nome}</td>
                    <td style={{ ...tdStyle, color: '#6b7280' }} className="hide-sm">{item.servicos_pagos}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: '#16a34a' }}>
                      {formatCurrency(item.valor_pago)}
                    </td>
                    <td style={tdStyle} className="hide-xs">{item.forma_pagamento}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <StatusBadge status={item.status_pagamento} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AccordionSection>
      {/* ══ Pagamentos Pendentes ══ */}
      <AccordionSection title="Pagamentos Pendentes" icon={ICONS.clock}>
        <div style={{ marginBottom: '14px' }}>
          <button
            type="button" onClick={fetchPagamentosPendentes} disabled={isLoadingPagamentosPendentes}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '8px', border: 'none',
              background: isLoadingPagamentosPendentes ? '#93c5fd' : 'linear-gradient(135deg, #1e3a5f, #2563eb)',
              color: '#fff', fontSize: '12px', fontWeight: '600',
              cursor: isLoadingPagamentosPendentes ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            }}
          >
            {isLoadingPagamentosPendentes
              ? <Spinner as="span" animation="border" size="sm" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
              : <Icon d={ICONS.refresh} size={11} color="#fff" />
            }
            Atualizar Lista
          </button>
        </div>

        {isLoadingPagamentosPendentes ? <LoadingRow /> : pagamentosPendentesData.length === 0 ? (
          <EmptyState message="Nenhum pagamento pendente encontrado." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle} className="hide-sm">Data Agend.</th>
                  <th style={thStyle}>Paciente</th>
                  <th style={thStyle} className="hide-sm">Serviço(s)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Valor Pendente</th>
                  <th style={thStyle} className="hide-xs">Vencimento</th>
                </tr>
              </thead>
              <tbody>
                {pagamentosPendentesData.map(item => (
                  <tr key={item.id} className="pag-tr">
                    <td style={tdStyle} className="hide-sm"><span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>{item.data_agendamento}</span></td>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>{item.paciente_nome}</td>
                    <td style={{ ...tdStyle, color: '#6b7280' }} className="hide-sm">{item.servicos_agendados}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: '#d97706' }}>{formatCurrency(item.valor_total_pendente)}</td>
                    <td style={tdStyle} className="hide-xs">
                      <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '999px', backgroundColor: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}>
                        {item.data_vencimento}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AccordionSection>

      {/* ══ Inadimplência ══ */}
      <AccordionSection title="Inadimplência" icon={ICONS.alert}>
        <div style={{ marginBottom: '14px' }}>
          <button
            type="button" onClick={fetchInadimplencia} disabled={isLoadingInadimplencia}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '8px', border: 'none',
              background: isLoadingInadimplencia ? '#93c5fd' : 'linear-gradient(135deg, #7f1d1d, #dc2626)',
              color: '#fff', fontSize: '12px', fontWeight: '600',
              cursor: isLoadingInadimplencia ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            }}
          >
            {isLoadingInadimplencia
              ? <Spinner as="span" animation="border" size="sm" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
              : <Icon d={ICONS.refresh} size={11} color="#fff" />
            }
            Atualizar Lista
          </button>
        </div>

        {isLoadingInadimplencia ? <LoadingRow /> : inadimplenciaData.length === 0 ? (
          <EmptyState message="Nenhuma inadimplência encontrada." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Paciente</th>
                  <th style={thStyle} className="hide-sm">Serviço(s)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Valor Aberto</th>
                  <th style={thStyle} className="hide-xs">Vencimento Original</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Dias Atraso</th>
                </tr>
              </thead>
              <tbody>
                {inadimplenciaData.map(item => (
                  <tr key={item.id} className="pag-tr">
                    <td style={{ ...tdStyle, fontWeight: '600' }}>{item.paciente_nome}</td>
                    <td style={{ ...tdStyle, color: '#6b7280' }} className="hide-sm">{item.servicos}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: '#dc2626' }}>{formatCurrency(item.valor_aberto)}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }} className="hide-xs">{item.data_vencimento_original}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '999px', backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
                        {item.dias_atraso}d
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AccordionSection>

      {/* ══ Por Forma de Pagamento ══ */}
      <AccordionSection title="Por Forma de Pagamento" icon={ICONS.money}>
        <FilterBar onFilter={fetchPagPorFormaPagamento} isLoading={isLoadingPagPorForma} filterLabel="Gerar Gráfico">
          <div style={{ minWidth: '150px' }}>
            <FieldLabel>Data Início</FieldLabel>
            <input style={inputStyle} type="date" value={filtroDataInicioFormaPag} onChange={e => setFiltroDataInicioFormaPag(e.target.value)} {...focusHandlers} />
          </div>
          <div style={{ minWidth: '150px' }}>
            <FieldLabel>Data Fim</FieldLabel>
            <input style={inputStyle} type="date" value={filtroDataFimFormaPag} onChange={e => setFiltroDataFimFormaPag(e.target.value)} {...focusHandlers} />
          </div>
        </FilterBar>

        {isLoadingPagPorForma ? <LoadingRow /> : pagPorFormaPagamentoData.labels.length > 0 ? (
          <div style={{ backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9', padding: '16px' }}>
            <div style={{ maxWidth: '480px', margin: '0 auto', height: '280px' }}>
              <Bar
                data={pagPorFormaPagamentoData}
                options={{
                  responsive: true, maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    y: { beginAtZero: true, ticks: { callback: v => formatCurrency(v), font: { size: 10 } }, grid: { color: '#f1f5f9' } },
                    x: { ticks: { font: { size: 11 } }, grid: { display: false } },
                  },
                }}
              />
            </div>
          </div>
        ) : (
          <EmptyState message="Gere o gráfico para visualizar os dados." />
        )}
      </AccordionSection>

      {/* ══ Contas a Receber (Convênios) ══ */}
      <AccordionSection title="Contas a Receber (Convênios)" icon={ICONS.shield}>
        <FilterBar onFilter={fetchContasAReceberConvenio} isLoading={isLoadingContasReceberConvenio}>
          <div style={{ minWidth: '140px' }}>
            <FieldLabel>Data Início</FieldLabel>
            <input style={inputStyle} type="date" value={filtroDataInicioConvenio} onChange={e => setFiltroDataInicioConvenio(e.target.value)} {...focusHandlers} />
          </div>
          <div style={{ minWidth: '140px' }}>
            <FieldLabel>Data Fim</FieldLabel>
            <input style={inputStyle} type="date" value={filtroDataFimConvenio} onChange={e => setFiltroDataFimConvenio(e.target.value)} {...focusHandlers} />
          </div>
          <div style={{ minWidth: '150px' }}>
            <FieldLabel>Convênio</FieldLabel>
            <input style={inputStyle} type="text" placeholder="Nome do convênio" value={filtroConvenioNome} onChange={e => setFiltroConvenioNome(e.target.value)} {...focusHandlers} />
          </div>
          <div style={{ minWidth: '130px' }}>
            <FieldLabel>Status</FieldLabel>
            <select style={{ ...inputStyle, appearance: 'auto' }} value={filtroStatusRecebimento} onChange={e => setFiltroStatusRecebimento(e.target.value)} {...focusHandlers}>
              <option value="">Todos</option>
              <option value="Pendente">Pendente</option>
              <option value="Recebido">Recebido</option>
              <option value="Glosa">Glosa</option>
            </select>
          </div>
        </FilterBar>

        {isLoadingContasReceberConvenio ? <LoadingRow /> : contasReceberConvenioData.length === 0 ? (
          <EmptyState message="Nenhuma conta a receber de convênios para o período." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle} className="hide-sm">Data Atend.</th>
                  <th style={thStyle}>Paciente</th>
                  <th style={thStyle} className="hide-xs">Convênio</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Valor a Receber</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {contasReceberConvenioData.map(item => (
                  <tr key={item.pagamento_id} className="pag-tr">
                    <td style={tdStyle} className="hide-sm"><span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>{item.data_atendimento}</span></td>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>{item.paciente_nome}</td>
                    <td style={tdStyle} className="hide-xs">{item.convenio_nome}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: '#2563eb' }}>{formatCurrency(item.valor_a_receber)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}><StatusBadge status={item.status_recebimento} /></td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {item.status_recebimento === 'Pendente' && (
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => onMarcarRecebidoClick(item)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #bbf7d0', backgroundColor: '#f0fdf4', color: '#16a34a', fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}
                            title="Marcar como Recebido"
                          >
                            <Icon d={ICONS.check} size={10} color="#16a34a" stroke={2.5} /> Recebido
                          </button>
                          <button
                            onClick={() => onMarcarGlosadoClick(item)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#dc2626', fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}
                            title="Marcar como Glosa"
                          >
                            <Icon d={ICONS.warn} size={10} color="#dc2626" stroke={2.5} /> Glosa
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AccordionSection>
    </div>
  );
};

export default RelatoriosPagamentosTab;
